import {
  Button,
  Card,
  Col,
  DatePicker,
  Layout,
  message,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Typography,
} from "antd";
import {
  FilterOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { getAgentsForFilter } from "../../../api/constants";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getAgentsStatistics } from "../../../api/statistics/statisticsRequest";
import { formatAmountInFrench } from "../../../utils/constants";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Content } = Layout;
const { Title } = Typography;

const AgentStatistics = () => {
  const [selectedSort, setSelectedSort] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [dateRange, setDateRange] = useState([]);

  const [users, setUsers] = useState([]);
  const [agentData, setAgentData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const filterAgentsStatistics = async () => {
    setLoading(true);
    try {
      // Add your API call here
      const data = await getAgentsStatistics({
        start_date:
          dateRange && dateRange[0]
            ? dateRange[0].format("YYYY-MM-DD HH:mm:ss")
            : null,
        end_date:
          dateRange && dateRange[1]
            ? dateRange[1].format("YYYY-MM-DD HH:mm:ss")
            : null,
        agent_id: selectedUser,
        sort_by: selectedSort,
      });
      setAgentData(data); // Update state with the fetched data
    } catch (error) {
      // Handle errors here
      setAgentData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await getAgentsForFilter();
      setUsers(data); // Update state with the fetched data
    } catch (error) {
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Agent",
      dataIndex: "full_name",
      key: "full_name",
    },
    {
      title: "Total des avertissements",
      dataIndex: "total_warnings",
      key: "total_warnings",
      align: "center",
      sorter: (a, b) => a.total_warnings - b.total_warnings,
    },
    {
      title: "Infractions Non Payées",
      dataIndex: "infractions_not_paid",
      key: "infractions_not_paid",
      align: "center",
      sorter: (a, b) => a.infractions_not_paid - b.infractions_not_paid,
    },
    {
      title: "Infractions Payées",
      dataIndex: "infractions_paid",
      key: "infractions_paid",
      align: "center",
      sorter: (a, b) => a.infractions_paid - b.infractions_paid,
    },
    {
      title: "Infractions Payées par Agent",
      dataIndex: "infractions_paid_by_agent",
      key: "infractions_paid_by_agent",
      align: "center",
      sorter: (a, b) => a.infractions_paid_by_agent - b.infractions_paid_by_agent,
    },
    {
      title: "Total des Tickets",
      dataIndex: "total_ticket_parkings",
      key: "total_ticket_parkings",
      align: "center",
      sorter: (a, b) => a.total_ticket_parkings - b.total_ticket_parkings,
    },
    {
      title: "Revenu Total",
      dataIndex: "total_revenue",
      key: "total_revenue",
      align: "right",
      sorter: (a, b) =>
        parseFloat(a.total_revenue) - parseFloat(b.total_revenue),
      render: (text) => `${formatAmountInFrench(parseFloat(text).toFixed(2))}`,
    },
  ];

  const chartOptions = {
    chart: {
      type: "column",
    },
    title: {
      text: "Revenu par Agent",
    },
    xAxis: {
      categories: agentData
        ? agentData.agents.map((agent) => agent.full_name)
        : [],
      crosshair: true,
    },
    credits: {
      enabled: false,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Revenu (MAD)",
      },
    },
    tooltip: {
      headerFormat: '<span style="font-size:10px">{point.key}</span><table>',
      pointFormat:
        '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y:.2f}</b></td></tr>',
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "Revenu",
        data: agentData
          ? agentData.agents.map((agent) => parseFloat(agent.total_revenue))
          : [],
      },
    ],
  };

  return (
    <Layout className="min-h-screen ">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={8}>
                <RangePicker
                  onChange={(dates) => setDateRange(dates)}
                  className="w-full"
                  showTime={{ format: "HH:mm" }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Filtrer par Constateur"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedUser(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  {users.map((type) => (
                    <Option key={type.id_agent} value={type.id_agent}>
                      {type.full_name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Select
                  placeholder="Trier par"
                  style={{ width: "100%" }}
                  onChange={(value) => setSelectedSort(value)}
                  allowClear
                >
                  {/* Populate this with options as needed */}
                  <Option key={1} value={"revenue"}>
                    Revenue
                  </Option>
                  <Option key={2} value={"warnings"}>
                    Avertissements
                  </Option>
                  <Option key={3} value={"ticket_parkings"}>
                    Ticket Parkings
                  </Option>
                  <Option key={4} value={"infractions_not_paid"}>
                    Infractions Non Payées
                  </Option>
                  <Option key={5} value={"infractions_paid"}>
                    Infractions Payées
                  </Option>
                  <Option key={6} value={"infractions_paid_by_agent"}>
                    Infractions Payées par Agent
                  </Option>
                </Select>
              </Col>
            </Row>
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterAgentsStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>

        {agentData && (
          <Card className="shadow-sm">
            <Space direction="vertical" size="large" className="w-full">
              <Title level={2} className="text-center mb-6">
                Résumé des Statistiques des Agents
              </Title>

              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Total des Agents Actifs"
                    value={agentData.summary.total_active_agents}
                    prefix={<TeamOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Total des avertissements"
                    value={agentData.summary.total_warnings}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Infractions Non Payées"
                    value={agentData.summary.total_infractions_not_paid}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Infractions Payées"
                    value={agentData.summary.total_infractions_paid}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Infractions Payées par Agents"
                    value={agentData.summary.total_infractions_paid_by_agents}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Total Ticket Parkings"
                    value={agentData.summary.total_ticket_parkings}
                    prefix={<FileTextOutlined />}
                  />
                </Col>
              </Row>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Revenu Total"
                    value={agentData.summary.total_revenue}
                    prefix={<DollarOutlined />}
                    precision={2}
                  />
                </Col>
                <Col xs={24} sm={12} md={4}>
                  <Statistic
                    title="Meilleur Performant"
                    value={agentData.summary.top_performer.full_name}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
              </Row>

              <Title level={3} className="mb-4">
                Revenu par Agent
              </Title>
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />

              <Title level={3} className="mt-6 mb-4">
                Statistiques Détaillées des Agents
              </Title>
              <Table
                dataSource={agentData.agents}
                columns={columns}
                rowKey="id_agent"
                pagination={false}
                className="w-full"
              />
            </Space>
          </Card>
        )}
      </Content>
    </Layout>
  );
};

export default AgentStatistics;
